import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from ".";
import { IAddItemBase, ICartItemData, IDirectPurchaseItemData } from "@interfaces";
import { cartApi } from "@api";
import { LoadingStage } from "src/Interfaces/shared";
import moment from "moment";
import { transformCartItemsToUpdateCartItemsData } from "src/Module/Checkout/checkoutHelpers";
interface BookNowState {
    cartItems: ICartItemData[];
    directPurchaseItemData: IDirectPurchaseItemData | null;
    cartItemsLoadingStage: LoadingStage;
}

const initialState: BookNowState = {
    cartItems: [],
    directPurchaseItemData: null,
    cartItemsLoadingStage: LoadingStage.NOT_INITIALIZED,
};

// Async thunk for adding to Book Now
export const addToBookNow = createAsyncThunk<ICartItemData[], IAddItemBase>(
    "bookNow/addToBookNow",
    async (addBookNowItem, { rejectWithValue }) => {
        try {
            const response = await cartApi.addCartDryRun(addBookNowItem);
            // Assign id from API or fallback to index
            const itemsWithId = (response.data.data as ICartItemData[]).map((item, idx) => ({
                ...item,
                id: item.id ?? idx,
            }));
            return itemsWithId;
        } catch (error: any) {
            console.error("Failed to add item in book now", error.response.data);
            return rejectWithValue(
                error.response?.data || error.message || "Failed to add item in book now",
            );
        }
    },
);

// Async thunk for completing Book Now purchase
export const completeBookNowPurchase = createAsyncThunk<void, IDirectPurchaseItemData>(
    "bookNow/completeBookNowPurchase",
    async (directPurchaseData, { rejectWithValue }) => {
        try {
            await cartApi.purchaseDirect(directPurchaseData);
        } catch (error) {
            console.error("Failed to complete purchase", error);
            return rejectWithValue("Failed to complete purchase");
        }
    },
);

const bookNowSlice = createSlice({
    name: "bookNow",
    initialState,
    reducers: {
        setBookNowItemData(state, action: PayloadAction<ICartItemData[]>) {
            state.cartItems = action.payload;

            const updates = transformCartItemsToUpdateCartItemsData(action.payload);
            if (state.cartItems.length === 0 || updates.length === 0) {
                state.directPurchaseItemData = null;
                return;
            }

            const productPricesDetails = state.cartItems.map((item, index) => {
                const update = updates[index];
                return {
                    productPricesDetailsId: String(item.product_price_details_id),
                    quantityDetails: [
                        {
                            quantity: update.quantity,
                            bookingDate: moment(item.booking_date).format("DD-MMM-YYYY"),
                            bookingData: update.bookingData,
                        } as any,
                    ],
                };
            });
            const firstItem = state.cartItems[0];
            state.directPurchaseItemData = {
                tdmsProductId: firstItem.tdms_product_id.toString(),
                productPricesDetails,
                startDate: moment(firstItem.start_date).format("DD-MMM-YYYY"),
                days: firstItem.days,
                selectedAvailableIndices: state.cartItems.map((item) => item.selected_index),
            };
        },

        clearBookNowItem(state) {
            state.cartItems = [];
            state.directPurchaseItemData = null;
            state.cartItemsLoadingStage = LoadingStage.NOT_INITIALIZED;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(addToBookNow.pending, (state) => {
                state.cartItemsLoadingStage = LoadingStage.IS_INITIALIZING;
            })
            .addCase(addToBookNow.fulfilled, (state, action) => {
                state.cartItems = action.payload;
                state.cartItemsLoadingStage = LoadingStage.INITIALIZE_COMPLETE;
            })
            .addCase(addToBookNow.rejected, (state) => {
                state.cartItemsLoadingStage = LoadingStage.NOT_INITIALIZED;
            });
    },
});

export const { setBookNowItemData, clearBookNowItem } = bookNowSlice.actions;
export const selectBookNow = (state: RootState) => state.bookNow;
export default bookNowSlice.reducer;
